import { Shape, ShapeType } from '../shape';
import { Stage } from 'konva/lib/Stage';
import { Node } from 'konva/lib/Node';
import { Group } from 'konva/lib/Group';
import { useRuntimeStore } from '@/store/use-runtime-store';
export class KonvaUtils {
  static bindKeyBoard(stage: Stage) {
    const container = stage.container();
    container.tabIndex = -1;
    container.focus({ preventScroll: true });
    // 只保持在stage mousedown时，让stage.container聚焦
    // 从而让其他shape(e.g. Text)在鼠标移动可以保持输入焦点
    stage.on('mousedown', () => {
      container.focus({ preventScroll: true });
    });
  }

  static isInteractableNode(node: Node) {
    return node == this.getInteractableNode(node);
  }

  /**
   * 获取可交互的rect节点
   * 情况1：普通节点则返回自身
   * 情况2：属于group的子元素，需要返回group或者自身（根据group explode状态）
   * @param node
   */
  static getInteractableNode(node: Node) {
    let parent = node.parent;

    while (
      parent &&
      parent?.getType()?.toLowerCase() === 'group' &&
      !parent.getAttr('data')?.explode
    ) {
      node = parent;
      parent = node.parent;
    }
    // 如果节点类型为Gruop需要返回group锚定的rect元素
    if (node.getType().toLowerCase() == 'group') {
      node = node.getAttr('data')?.rect;
    }
    return node;
  }

  /**
   * 解散group，即将子元素释放，变为可交互
   * @param group
   * @param explode
   */
  static toggleGroupExplode = (group: Group, explode: boolean) => {
    // 如果是其他group则处理数据
    const data = group.getAttr('data') ?? {};
    data.explode = explode;
    group.setAttr('data', data);
  };

  /**
   * 返回节点node是否enable状态，即未冻结，隐藏
   * @param node
   * @returns
   */
  static isNodeEnabled(node: Node) {
    if (node.id() !== 'shape') return;
    const name = node.name();
    if (!name) return false;
    const shape = Shape.getShape(name);
    // 为解决组内子元素无法选中问题，当锁定时，不处理
    // if (shape?.attrs._isLocked) return false;
    if (!shape?.attrs._visibility) return false;
    return true;
  }

  static getRuntimData(shapeId: string) {
    const runtimeStore = useRuntimeStore();
    return runtimeStore.shapeState(shapeId);
  }

  /**
   * 获取图片元素的实际渲染宽高
   * 优先使用 runtime.width/height（绑定数据源后的实际宽高）
   * 如果没有 runtime 数据，则使用 attrs.w/h
   *
   * @param shape 图片 shape
   * @returns 实际宽高
   */
  static getImageActualSize(shape: Shape): { width: number; height: number } {
    const runtime = KonvaUtils.getRuntimData(shape.id);
    const width = runtime?.width ?? shape.attrs.w ?? 0;
    const height = runtime?.height ?? shape.attrs.h ?? 0;
    return { width, height };
  }

  /**
   * 获取图片元素的默认 pivot（旋转中心点）
   * 基于实际渲染宽高计算，确保修改 pivotX/Y 时位置不变
   *
   * @param shape 图片 shape
   * @returns 默认 pivot 坐标
   */
  static getImageDefaultPivot(shape: Shape): { x: number; y: number } {
    const { width, height } = KonvaUtils.getImageActualSize(shape);
    return {
      x: width / 2,
      y: height / 2
    };
  }

  static getRuntimeSlotSize(slot: Shape) {
    const _getWidgetSize = (widget: Shape) => {
      // 获取widget的宽高
      let minX = 0;
      let minY = 0;
      let maxX = -Infinity;
      let maxY = -Infinity;
      widget.children?.forEach((kid) => {
        const type = kid.type;
        const runtimeData = this.getRuntimData(kid.id);
        const {
          offsetX = 0,
          offsetY = 0,
          width: _w = 0,
          height: _h = 0
        } = runtimeData ?? {};

        const { w: width = 0, h: height = 0 } =
          type == ShapeType.Widget ? _getWidgetSize(kid) : { w: _w, h: _h };

        let _offsetX = offsetX;
        // 处理图组align
        const align = kid.attrs.align;
        if (kid.type == ShapeType.ImageArray && align) {
          _offsetX -=
            align == 'center' ? width / 2 : align == 'right' ? width : 0;
        }
        minX = Math.min(minX, _offsetX);
        minY = Math.min(minY, offsetY);
        maxX = Math.max(maxX, _offsetX + width);
        maxY = Math.max(maxY, offsetY + height);
      });
      const w = maxX == -Infinity ? 100 : maxX - minX;
      const h = maxY == -Infinity ? 100 : maxY - minY;

      const { w: w0 = 0, h: h0 = 0 } = widget.attrs;
      // 最后取实际渲染尺寸和widget上自定义的尺寸的较大值
      return { w: Math.max(w, w0), h: Math.max(h, h0) };
    };

    const currWidget = Shape.getShape(slot?.attrs.widgetIdToDisplay ?? '');
    if (currWidget) {
      return _getWidgetSize(currWidget);
    }
  }
}
